import { COMPANY_SIGN_IN_SETTINGS_PATH, ssoProviderSlugSchema } from '@instanexus/contracts';
import { errorMessage, useTranslation } from '@instanexus/i18n';
import { isApiError } from '@instanexus/sdk';
import { Button, Card, CardDescription, CardFooter, CardHeader } from '@instanexus/ui';
import { setAccessToken } from '@instanexus/web-auth';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router';

import { AuthCardLayout } from '@/components/auth-card-layout';
import { useApiClient } from '@/hooks/use-api-client';
import { centralLoginPath } from '@/lib/auth-paths';
import { startCompanySignInTest } from '@/lib/company-sign-in-test';
import { clientAppOrigin } from '@/lib/sign-in-request';

/** Not a declared `ErrorCode`; `errorMessage()` falls back to `errors:unexpected`. */
const UNEXPECTED_FAILURE = 'UNEXPECTED';

/** Company sign-in settings live in platform-admin, so the test always belongs to the admin client. */
const CLIENT = 'admin';

type Phase = 'starting' | 'signed-out' | 'error';

function redirectTo(url: string): void {
  window.location.assign(url);
}

/** `/company-sign-in/test?client=admin&provider=google` — starts the admin's company test sign-in (ADR 0042 decision 5). */
export function CompanySignInTestPage(): ReactElement {
  const { t } = useTranslation('auth');
  const api = useApiClient();
  const [searchParams] = useSearchParams();
  const [phase, setPhase] = useState<Phase>('starting');
  const [errorCode, setErrorCode] = useState<string>(UNEXPECTED_FAILURE);

  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const provider = ssoProviderSlugSchema.safeParse(searchParams.get('provider'));
    if (!provider.success) {
      setErrorCode('SSO_PROVIDER_UNAVAILABLE');
      setPhase('error');
      return;
    }
    void (async (): Promise<void> => {
      try {
        const session = await api.auth.refreshSession();
        if (session === undefined) {
          setPhase('signed-out');
          return;
        }
        setAccessToken(session.accessToken);
        await startCompanySignInTest(api, provider.data, CLIENT, redirectTo);
      } catch (error: unknown) {
        setErrorCode(isApiError(error) ? error.code : UNEXPECTED_FAILURE);
        setPhase('error');
      }
    })();
  }, [api, searchParams]);

  const origin = clientAppOrigin(CLIENT);
  const backUrl =
    origin === null ? null : new URL(COMPANY_SIGN_IN_SETTINGS_PATH, origin).toString();

  return (
    <AuthCardLayout>
      <Card className="w-full max-w-md">
        <CardHeader>
          <h1 className="text-3xl font-semibold text-primary text-balance">
            {t('companySignInTest.title')}
          </h1>
          {phase === 'starting' ? (
            <p role="status" className="text-sm text-muted-foreground">
              {t('companySignInTest.starting')}
            </p>
          ) : null}
          {phase === 'signed-out' ? (
            <CardDescription>{t('companySignInTest.signedOutBody')}</CardDescription>
          ) : null}
          {phase === 'error' ? (
            <CardDescription role="alert">{errorMessage(t, errorCode)}</CardDescription>
          ) : null}
        </CardHeader>
        {phase === 'signed-out' ? (
          <CardFooter>
            <Button asChild>
              <Link to={centralLoginPath(CLIENT)}>{t('companySignInTest.signIn')}</Link>
            </Button>
          </CardFooter>
        ) : null}
        {phase === 'error' && backUrl !== null ? (
          <CardFooter>
            <Button asChild variant="outline">
              <a href={backUrl}>{t('companySignInTest.back')}</a>
            </Button>
          </CardFooter>
        ) : null}
      </Card>
    </AuthCardLayout>
  );
}
